import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Tag, Folder } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

const DEFAULT_ACCENTS = ["#9c6dde", "#b9985f", "#c93875", "#2eb6a8", "#e8478e", "#1f8d83"];

export default function SectionsManager({ onChange }) {
  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category_slug: "", accent_color: "#9c6dde" });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    const [s, c] = await Promise.all([
      api.get("/admin/sections"),
      api.get("/admin/categories"),
    ]);
    setSections(s.data);
    setCategories(c.data);
  };

  useEffect(() => { fetchAll().catch(() => {}); }, []);

  const startNew = () => {
    const first = categories[0]?.slug || "";
    setForm({ name: "", category_slug: first, accent_color: DEFAULT_ACCENTS[sections.length % DEFAULT_ACCENTS.length] });
    setEditing("new");
  };
  const startEdit = (s) => {
    setForm({ name: s.name, category_slug: s.category_slug, accent_color: s.accent_color });
    setEditing(s);
  };
  const close = () => { setEditing(null); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/sections", form);
        toast.success("Section created!");
      } else {
        await api.patch(`/admin/sections/${editing.id}`, form);
        toast.success("Section updated!");
      }
      await fetchAll();
      onChange?.();
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete section "${s.name}"? Products will be untagged.`)) return;
    try {
      await api.delete(`/admin/sections/${s.id}`);
      toast.success("Deleted");
      await fetchAll();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  // Group by parent category
  const grouped = categories.map((c) => ({
    category: c,
    items: sections.filter((s) => s.category_slug === c.slug),
  })).filter((g) => g.items.length > 0 || categories.length > 0);

  return (
    <div data-testid="sections-manager">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <div className="font-script text-2xl text-[#c93875]">Genre by Genre</div>
          <h2 className="font-display font-extrabold text-2xl text-[#1c1815]">Sections</h2>
          <p className="text-[13px] text-[#8a8076] mt-1">Group products into sub-genres under each department (e.g. "Western Designs" under Digital Downloads).</p>
        </div>
        <button onClick={startNew} data-testid="section-new" disabled={categories.length === 0}
          className="inline-flex items-center gap-2 bg-[#9c6dde] hover:bg-[#7d52b8] text-white font-bold uppercase tracking-[0.2em] text-[11px] px-5 py-3 rounded-full transition-all disabled:opacity-40">
          <Plus size={14}/> New Section
        </button>
      </div>

      {categories.length === 0 && (
        <div className="p-6 rounded-2xl border border-dashed border-[#e5dccd] text-center text-[#8a8076]">
          Create at least one category first — sections live under categories.
        </div>
      )}

      <div className="space-y-6">
        {grouped.map((g) => (
          <div key={g.category.id} data-testid={`sections-group-${g.category.slug}`} className="bg-white border border-[#e5dccd] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Folder size={14} style={{ color: g.category.accent_color }}/>
              <h3 className="font-display font-extrabold text-lg text-[#1c1815]">{g.category.name}</h3>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8a8076] ml-1">({g.items.length})</span>
            </div>
            {g.items.length === 0 ? (
              <div className="text-[12px] text-[#8a8076] italic">No sections yet — click "New Section" and pick {g.category.name}.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {g.items.map((s) => (
                  <div key={s.id} data-testid={`section-row-${s.slug}`}
                    className="group inline-flex items-center gap-2 bg-[#faf4e9] border border-[#e5dccd] rounded-full pl-3 pr-1.5 py-1.5">
                    <Tag size={11} style={{ color: s.accent_color }}/>
                    <span className="text-[12px] font-bold text-[#1c1815]">{s.name}</span>
                    <button onClick={() => startEdit(s)} data-testid={`section-edit-${s.slug}`}
                      title="Edit"
                      className="p-1 hover:bg-white rounded-full text-[#2eb6a8] transition-colors">
                      <Pencil size={11}/>
                    </button>
                    <button onClick={() => del(s)} data-testid={`section-delete-${s.slug}`}
                      title="Delete"
                      className="p-1 hover:bg-white rounded-full text-[#c93875] transition-colors">
                      <Trash2 size={11}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 sm:p-8 overflow-y-auto bg-[#1c1815]/45 backdrop-blur-sm" onClick={close}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white border border-[#e5dccd] rounded-3xl w-full max-w-lg p-7 my-8 shadow-2xl" data-testid="section-form">
            <h2 className="font-display font-extrabold text-2xl text-[#1c1815] mb-5">
              {editing === "new" ? "New Section" : "Edit Section"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Name</label>
                <input type="text" required value={form.name} data-testid="section-name"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Western Designs, Trendy Mama"
                  className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#9c6dde] rounded-xl px-4 py-3 outline-none text-[#1c1815]"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Parent Category</label>
                <select required value={form.category_slug} data-testid="section-category"
                  onChange={(e) => setForm({ ...form, category_slug: e.target.value })}
                  className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#9c6dde] rounded-xl px-4 py-3 outline-none text-[#1c1815]">
                  {categories.map((c) => (
                    <option key={c.id} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Accent Color</label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {DEFAULT_ACCENTS.map((c) => (
                    <button key={c} type="button" data-testid={`section-color-${c}`}
                      onClick={() => setForm({ ...form, accent_color: c })}
                      style={{ backgroundColor: c }}
                      className={`w-9 h-9 rounded-full border-2 transition-all ${form.accent_color === c ? "border-[#1c1815] scale-110" : "border-white"}`}
                    />
                  ))}
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="section-submit"
                className="w-full bg-[#9c6dde] hover:bg-[#7d52b8] text-white font-bold uppercase tracking-[0.2em] text-xs py-3.5 rounded-full transition-all disabled:opacity-60">
                {saving ? "Saving…" : (editing === "new" ? "Create Section" : "Save Changes")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
